import React, { useState, ChangeEvent, useEffect } from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Button,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";

import { getCategories } from "../../../../api/providers/plannerEventsProviders";
import {
  EventData as EventDataInterface,
  Category,
} from "../../../../interfaces/planner.interface";

const mapContainerStyle = {
  width: "100%",
  height: "250px",
};

const defaultCenter = {
  lat: 9.953158,
  lng: -84.086716,
};

interface EventDataProps {
  eventData: EventDataInterface;
  setEventData: React.Dispatch<React.SetStateAction<EventDataInterface>>;
  setLoading: (loading: boolean) => void;
}

export const EventData: React.FC<EventDataProps> = ({
  eventData,
  setEventData,
  setLoading,
}) => {
  const [categories, setCategories] = useState<Category | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_MAPS_KEY || "",
  });

  const handleLocationChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setEventData((prevData) => ({
      ...prevData,
      address: value,
    }));

    if (value) {
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
        value
      )}&key=${import.meta.env.VITE_MAPS_KEY}`;

      try {
        console.log(geocodeUrl);
        const response = await fetch(geocodeUrl);
        const data = await response.json();

        if (data.results && data.results.length > 0) {
          const location = data.results[0].geometry.location;
          setEventData((prevData) => ({
            ...prevData,
            latitude: location.lat,
            longitude: location.lng,
          }));
        } else {
          console.error("No results found for the given address.");
          setEventData((prevData) => ({
            ...prevData,
            latitude: 9.953158,
            longitude: -84.086716,
          }));
        }
      } catch (error) {
        console.error("Error fetching geocode data:", error);
        setEventData((prevData) => ({
          ...prevData,
          latitude: 9.953158,
          longitude: -84.086716,
        }));
      }
    } else {
      setEventData((prevData) => ({
        ...prevData,
        latitude: 9.953158,
        longitude: -84.086716,
      }));
    }
  };

  const handleCategories = async () => {
    try {
      setLoading(true);
      const result = await getCategories();
      if (result.ok) {
        setCategories(result.data);
      }
    } catch (error) {
      console.error("Error al obtener las categorias", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleCategories();
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEventData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCategoryChange = (event: ChangeEvent<{ value: unknown }>) => {
    const categoryId = event.target.value as number;
    setEventData((prevData) => ({
      ...prevData,
      categoryId: categoryId,
    }));
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEventData((prevData) => ({
          ...prevData,
          bannerAlt: { file: file, preview: reader.result as string },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEventData((prevData) => ({
          ...prevData,
          banner: { file: file, preview: reader.result as string },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      setEventData((prevData) => ({
        ...prevData,
        latitude: lat,
        longitude: lng,
      }));

      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${
        import.meta.env.VITE_MAPS_KEY
      }`;
      try {
        const response = await fetch(geocodeUrl);
        const data = await response.json();
        if (data.results && data.results.length > 0) {
          const address = data.results[0].formatted_address;
          setEventData((prevData) => ({
            ...prevData,
            address: address,
          }));
        } else {
          console.error("No address found for the given coordinates.");
          setEventData((prevData) => ({
            ...prevData,
            address: "",
          }));
        }
      } catch (error) {
        console.error("Error fetching address data:", error);
        setEventData((prevData) => ({
          ...prevData,
          address: "",
        }));
      }
    }
  };

  const mapCenter =
    eventData.latitude && eventData.longitude
      ? { lat: eventData.latitude, lng: eventData.longitude }
      : defaultCenter;

  if (!isLoaded) return null;

  return (
    <Grid container spacing={2} sx={{ marginTop: 2 }}>
      <Grid item xs={12}>
        <Typography fontSize={32} align="left">
          Datos generales
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography
          fontSize={24}
          
          align="left"
          sx={{ marginBottom: 2 }}
        >
          Imágenes del evento
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                width: "100%",
                height: 266,
                backgroundColor: eventData.bannerAlt.preview
                  ? "transparent"
                  : "grey",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 2,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {!eventData.bannerAlt.preview && (
                <Typography color="white" variant="subtitle1">
                  Imagen principal
                </Typography>
              )}

              {eventData.bannerAlt.preview && (
                <img
                  src={eventData.bannerAlt.preview}
                  alt="Imagen principal"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}

              <Button
                variant="contained"
                component="label"
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  backgroundColor: "lightgreen",
                  borderRadius: "10%",
                }}
              >
                <EditIcon />
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleMainImageChange}
                />
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box
              sx={{
                width: "100%",
                height: 266,
                backgroundColor: eventData.banner.preview
                  ? "transparent"
                  : "grey",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 2,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {!eventData.banner.preview && (
                <Typography color="white" variant="subtitle1">
                  Banner
                </Typography>
              )}
              {eventData.banner.preview && (
                <img
                  src={eventData.banner.preview || ""}
                  alt="Banner"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
              <Button
                variant="contained"
                component="label"
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  backgroundColor: "lightgreen",
                  borderRadius: "10%",
                }}
              >
                <EditIcon />
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleBannerImageChange}
                />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <Grid container item spacing={2} sx={{ alignItems: "flex-start" }}>
        <Grid item xs={12} md={6}>
          <Typography fontSize={24} align="left">
            Datos del evento
          </Typography>
          <TextField
            fullWidth
            label="Título del evento"
            margin="normal"
            name="name"
            value={eventData.name}
            onChange={handleInputChange}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Fecha y hora de inicio"
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                name="startDate"
                value={eventData.startDate}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Fecha y hora de fin"
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                name="endDate"
                value={eventData.endDate}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>

          <FormControl fullWidth margin="normal">
            <InputLabel>Categoría del evento</InputLabel>
            <Select
              value={eventData.categoryId || ""}
              label="Categoría del evento"
              onChange={handleCategoryChange}
            >
              {categories &&
                categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Right side - Event Location */}
        <Grid item xs={12} md={6}>
          <Typography fontSize={24} align="left">
            Lugar del evento
          </Typography>
          <TextField
            fullWidth
            label="Dirección del lugar"
            margin="normal"
            name="address"
            value={eventData.address}
            onChange={handleLocationChange}
          />
          <Box sx={{ marginTop: 2 }}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={15}
              center={mapCenter}
              onClick={handleMapClick}
            >
              <Marker
                position={{ lat: eventData.latitude, lng: eventData.longitude }}
                icon={{
                  url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                }}
              />
            </GoogleMap>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};
